import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/primitives";
import { useLanguage } from "@/hooks/use-language";

const DURATION = 4000;

export const Manifesto = () => {
  const { t, lang } = useLanguage();
  const STEPS = t.stepper.steps;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const tm = setInterval(() => setActive((a) => (a + 1) % STEPS.length), DURATION);
    return () => clearInterval(tm);
  }, [paused, STEPS.length]);

  const step = STEPS[active];

  return (
    <section id="platform" className="py-24 lg:py-32 border-b border-border" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <SectionTag index="02" label={t.stepper.tag} />
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl max-w-3xl">
            {t.stepper.h2}
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            className="mt-14 border border-border"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="task-stepper"
          >
            <div className="grid grid-cols-2 md:grid-cols-5">
              {STEPS.map((s, i) => (
                <button
                  key={s.n}
                  onClick={() => setActive(i)}
                  data-testid={`step-tab-${s.n}`}
                  className={`relative text-left px-5 py-5 border-border transition-colors duration-300 ${
                    i >= 2 ? "border-t md:border-t-0 md:border-l" : i === 1 ? "border-l" : ""
                  } ${active === i ? "bg-secondary/70" : "hover:bg-secondary/40"}`}
                >
                  <span className={`font-display text-xs tracking-[0.25em] ${active === i ? "text-teal-600 dark:text-teal-400" : "text-muted-foreground"}`}>
                    {s.n}
                  </span>
                  <span className={`mt-1.5 block font-display font-bold text-sm leading-snug ${active === i ? "text-foreground" : "text-muted-foreground"}`}>
                    {s.label}
                  </span>
                  {active === i && !paused && (
                    <motion.span
                      key={`${lang}-${active}`}
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: DURATION / 1000, ease: "linear" }}
                      className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-teal-500 to-green-500"
                    />
                  )}
                  {active === i && paused && (
                    <span className="absolute bottom-0 left-0 h-0.5 w-full bg-gradient-to-r from-teal-500 to-green-500" />
                  )}
                </button>
              ))}
            </div>

            <div className="border-t border-border px-6 lg:px-10 py-10 min-h-[150px] flex items-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${lang}-${active}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="flex flex-col md:flex-row md:items-center gap-4 md:gap-10"
                  data-testid="step-detail"
                >
                  <span className="font-display font-black text-5xl text-gradient leading-none shrink-0">
                    {step.n}
                  </span>
                  <div>
                    {step.quote && (
                      <p className="text-sm font-medium text-teal-600 dark:text-teal-400 italic mb-2">{step.quote}</p>
                    )}
                    <p className="text-base md:text-lg text-muted-foreground max-w-2xl">{step.body}</p>
                  </div>
                  <ChevronRight size={20} className="hidden md:block text-teal-500/60 shrink-0 ml-auto" />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
